import { ScoreBenchmark } from 'features/site-selection/common/types/score-benchmark';
import {
    BENCHMARK_TYPE_OPTIONS,
    BENCHMARK_TYPE_TITLES,
} from 'features/site-selection/sections/top-matches-page/top-matches-filters/constants';
import { FilterSelect } from 'features/site-selection/common/types/filters/filters';
import type {
    MedianType,
    ReportAccessWithError,
    TopSiteInfo,
    ViewStateResponse,
} from 'features/site-selection/sections/top-matches-page/types';
import { MAGNITUDES, numberFormat } from 'utils/number/number';
import { SiteCreationMethod } from 'features/site-selection/common/constants/top-matches';
import { startCase } from 'lodash/fp';
import type { CategoryInfo } from '@placer-ui/types';
import type { PrimaryGrade } from 'ui-components/school-grade/utils-school-grade';
import { CascadingItem } from '@placer-ui/components';
import { ConfigurationCategoriesData } from 'features/insights/pages/visual-templates/types';
import { EMPTY_SPACE_COORDINATE_ID } from 'features/empty-space/constants/empty-space-constants';
import {
    ADDRESS_IN_RESTRICTED_AREA,
    COORDINATE_IN_RESTRICTED_AREA,
} from 'API/report-access-api/constants';
import { ReportAccessCoordinate } from 'API/report-access-api/report-access-api';
import { ResponseError, ResponseStatus } from 'API/common/types';
import { getRunBtnTooltipText } from 'features/empty-space/utils/utils';
import { ADDRESS_TITLE } from 'core/constants/address';
import { BasePlaceEntity } from '@placer-ui/types/src/entities/base-place-entity';

type getSiteTypeCategoryProps = {
    groupCategory?: string;
    isAddedSite?: boolean;
    creationMethod?: SiteCreationMethod | null;
};
export const getSiteTypeCategory = ({
    groupCategory,
    isAddedSite,
    creationMethod,
}: getSiteTypeCategoryProps) => {
    if (isAddedSite && creationMethod) {
        return startCase(creationMethod);
    }
    return groupCategory !== 'Region' ? 'Shopping Center' : 'Retail Node';
};

type getSiteTypeCategoryDisplayStringProps = {
    category?: Required<CategoryInfo> | null;
    isAddedSite?: boolean;
    creationMethod?: SiteCreationMethod | null;
};

export const getSiteTypeCategoryDisplayString = ({
    category,
    isAddedSite,
    creationMethod,
}: getSiteTypeCategoryDisplayStringProps): string | null => {
    if (category) {
        return category.primary.replace('/', ' / ');
    } else if (isAddedSite && creationMethod) {
        return startCase(creationMethod);
    } else {
        return null;
    }
};

export const getSiteRegionName = (scoreBenchmark?: FilterSelect, medianType?: MedianType) => {
    const regionType = scoreBenchmark?.value as ScoreBenchmark;
    const isScoreBenchmarkNationwide =
        regionType === BENCHMARK_TYPE_OPTIONS.nationwide ||
        regionType === BENCHMARK_TYPE_OPTIONS.chainNationwide;

    const regionTypeTitle =
        BENCHMARK_TYPE_TITLES[isScoreBenchmarkNationwide ? 'nationwide' : regionType];
    const regionTitleDisplay = isScoreBenchmarkNationwide
        ? regionTypeTitle.toLowerCase()
        : regionTypeTitle;

    return medianType === 'chain' || !medianType || isScoreBenchmarkNationwide
        ? 'nationwide'
        : `in the ${regionTitleDisplay}`;
};

export const populationNumberFormatter = (value: number): string => {
    const fractionDigits = value >= MAGNITUDES.million ? 1 : 0;
    return numberFormat(value, fractionDigits, true, true);
};

export function isViewStateResponseV2(
    data: ViewStateResponse<any> | any,
): data is ViewStateResponse<any> {
    return typeof data.id === 'string' && typeof data.state === 'object';
}

export function getQueryParams() {
    const queryParamsString = window.location.search;
    return new URLSearchParams(queryParamsString);
}

type AtlasIconNameProps = {
    mapType: 'roadmap' | 'hybrid';
    pinType: 'grade' | 'site' | 'venue';
    /** icon size */
    size?: 'regular' | 'big';
    /** variant of a favorite site (saved) or not */
    isFavorite?: boolean;
    /** if it's a grade icon then defined which grade */
    grade?: PrimaryGrade;
    /** if it's a venue icon then define if it has trade-area */
    hasTradeArea?: boolean;
    /** if chain/region wasn't paid for */
    requireUpsell?: boolean;
    /** if site is restricted due to state regulations and such */
    isRestricted?: boolean;
};
export function getAtlasIconName({
    mapType,
    pinType,
    size = 'regular',
    isFavorite = false,
    grade = 'A',
    hasTradeArea = true,
    requireUpsell = false,
    isRestricted = false,
}: AtlasIconNameProps) {
    let iconName = '';

    if (mapType === 'hybrid') iconName += 'hybrid_';
    else iconName += 'roadmap_';

    if (size === 'big') iconName += 'big_';

    if (requireUpsell) {
        iconName += 'promotion';
    } else if (isRestricted) {
        iconName += 'restricted_site';
    } else if (pinType === 'grade') {
        if (isFavorite) iconName += 'saved_';
        iconName += grade.toLowerCase();
    } else if (pinType === 'site') {
        iconName += 'site';
    } else {
        // is venue icon
        iconName += 'venue';
        if (!hasTradeArea) iconName += '_no-ta';
    }

    return iconName;
}

export const modifyCategoryData = (
    categoriesData: ConfigurationCategoriesData[],
    level: number,
    maxLevel?: number,
) => {
    return categoriesData.map(({ children, id, label }) => {
        const lastLevel = maxLevel && maxLevel === level;
        const subItems = !lastLevel
            ? children && modifyCategoryData(children, level + 1, maxLevel)
            : undefined;
        const item: CascadingItem = {
            value: id,
            label,
            isSelected: false,
            selected: 0,
            children: subItems,
            childrenExpanded: false,
        };

        return item;
    });
};

export const getIsSiteLightRisk = (site: TopSiteInfo) => {
    return site.warnings?.includes('light_risky_ft_removed');
};

export const getRestrictedAreaMessage = (
    reportAccess?: ReportAccessWithError,
    isAddress?: boolean,
) => {
    const hasReportAccessResponse = (reportAccess as ReportAccessCoordinate)?.access;
    const hasReportAccessError =
        (reportAccess as ResponseError)?.common || (reportAccess as ResponseStatus);

    if (!hasReportAccessResponse && hasReportAccessError) {
        return getRunBtnTooltipText(
            hasReportAccessError.type,
            !isAddress ? EMPTY_SPACE_COORDINATE_ID : ADDRESS_TITLE,
        );
    } else if (hasReportAccessResponse?.warnings.includes('heavy_risky_ft_removed')) {
        if (isAddress) {
            return ADDRESS_IN_RESTRICTED_AREA;
        }
        return COORDINATE_IN_RESTRICTED_AREA;
    } else {
        return '';
    }
};

export const getIsSupportedProperty = (property: BasePlaceEntity): boolean => {
    return property.type !== 'billboard' && property.entity_section !== 'market';
};

export const getIsPermittedEntity = (property: BasePlaceEntity) => {
    return property.purchased;
};
